/** AccountSwitch.tsx — 직원 계정으로 전환해 쓰기(2026-10-26 · backend services/accountSwitch)
 *  지도사 「임우승으로 로그인해도 직원 계정으로 전환해서 이용하는 버튼 · 비밀번호 입력해서 안 들어가더라도」
 *
 *  AccountBadge — 화면 위 이름 배지. 슈퍼관리자(또는 전환해 쓰는 중)면 눌러서 전환 메뉴가 열린다. 아니면 이름만.
 *  ActingBar    — 전환해 쓰는 동안 화면 아래에 늘 떠 있는 띠 「오재인 계정으로 쓰는 중 · 임우승으로 돌아가기」.
 *  전환하면 토큰 · 계정을 바꿔 끼우고 첫 화면부터 다시 연다(그 계정의 메뉴 · 권한 그대로).
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { backToOrigin, rememberOrigin } from './sessionGuard';

interface Me { id?: string; name?: string; username?: string; isSuper?: boolean; canSwitch?: boolean; actingBy?: { id: string; name: string } | null }
interface Target { id: string; name: string; username: string; self: boolean; current: boolean }
interface Targets { origin: { id: string; name: string }; list: Target[] }

const NAVY = '#1B3A5C';
const BASE = '/api/auth';

function stored(): Me | null {
  try { return JSON.parse(localStorage.getItem('account') || 'null') as Me | null; } catch (e) { return null; }
}
async function call<T>(method: 'GET' | 'POST', path: string): Promise<T> {
  let tok = '';
  try { tok = localStorage.getItem('token') || ''; } catch (e) { tok = ''; }
  const r = await fetch(BASE + path, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` } });
  const j = await r.json().catch(() => ({})) as { success?: boolean; data?: T; error?: string };
  if (!r.ok || !j.success) throw new Error(j.error || '전환하지 못했어요');
  return j.data as T;
}

/** 전환 — 토큰 · 계정을 바꿔 끼우고 첫 화면부터 */
async function switchTo(id: string): Promise<void> {
  const d = await call<{ token: string; account: Me }>('POST', `/switch/${id}`);
  rememberOrigin(d.token);   // 내 토큰을 맡겨 둠(전환 토큰이 끝나도 돌아올 수 있게 · sessionGuard)
  localStorage.setItem('token', d.token);
  localStorage.setItem('account', JSON.stringify(d.account));
  try { sessionStorage.removeItem('tm_gov_prog'); } catch (e) { /* 없어도 됨 */ }
  window.location.assign('/');
}

/** 받침 있으면 「으로」(ㄹ 받침은 「로」) — 임우승으로 · 오재인으로 · 김하늘로 */
const ro = (w: string) => { const c = w.charCodeAt(w.length - 1) - 0xAC00; if (c < 0 || c > 11171) return `${w}로`; const j = c % 28; return j === 0 || j === 8 ? `${w}로` : `${w}으로`; };

const canSwitch = (me: Me | null) => !!me && (!!me.isSuper || !!me.canSwitch || !!me.actingBy);

/** 전환 메뉴 — 배지 아래로 열림(좁은 화면은 아래에서 올라오는 판) */
function SwitchMenu({ onClose, anchor }: { onClose: () => void; anchor: DOMRect | null }) {
  const [data, setData] = useState<Targets | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const narrow = window.innerWidth < 560;
  useEffect(() => {
    let live = true;
    call<Targets>('GET', '/switch-targets').then((d) => { if (live) setData(d); }).catch((e: unknown) => { if (live) setErr(e instanceof Error ? e.message : '불러오지 못했어요'); });
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => { live = false; window.removeEventListener('keydown', esc); };
  }, [onClose]);
  const go = async (t: Target) => {
    if (t.current || busy) return;
    setBusy(t.id);
    try { await switchTo(t.id); } catch (e) { setErr(e instanceof Error ? e.message : '전환하지 못했어요'); setBusy(''); }
  };
  const box: CSSProperties = narrow
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, borderRadius: '14px 14px 0 0', maxHeight: '70vh' }
    : { position: 'fixed', top: (anchor ? anchor.bottom : 46) + 6, right: anchor ? Math.max(8, window.innerWidth - anchor.right) : 16, width: 280, borderRadius: 12, maxHeight: '70vh' };
  return (
    <>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 3000, background: narrow ? 'rgba(17,24,39,.4)' : 'transparent' }} />
      <div role="dialog" aria-label="직원 계정으로 전환" style={{ ...box, zIndex: 3001, background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 12px 32px rgba(17,24,39,.18)', overflowY: 'auto', fontFamily: 'inherit' }}>
        <div style={{ padding: '14px 16px 8px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>다른 계정으로 쓰기</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, wordBreak: 'keep-all' }}>비밀번호 없이 그 계정 화면으로 들어가요 · 화면 아래 띠에서 돌아와요</div>
        </div>
        {err && <div role="alert" style={{ margin: '0 16px 8px', fontSize: 12, color: '#B42318' }}>{err}</div>}
        {!data && !err && <div style={{ padding: '8px 16px 14px', fontSize: 12, color: '#6B7280' }}>불러오는 중…</div>}
        {data && <div role="list" style={{ padding: '0 8px 10px' }}>
          {data.list.map((t) => (
            <button key={t.id} type="button" role="listitem" disabled={t.current || !!busy} onClick={() => void go(t)}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, padding: '0 10px', border: 'none', borderRadius: 8, background: t.current ? '#EEF3F8' : '#fff', cursor: t.current ? 'default' : 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <b style={{ fontSize: 13, color: '#111827' }}>{t.name}</b>
                <span style={{ fontSize: 12, color: '#6B7280' }}>{t.self ? ' · 내 계정' : ` · ${t.username}`}</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: t.current ? NAVY : '#6B7280', whiteSpace: 'nowrap' }}>{busy === t.id ? '여는 중…' : t.current ? '지금 쓰는 중' : t.self ? '돌아가기' : '전환'}</span>
            </button>
          ))}
        </div>}
      </div>
    </>
  );
}

/** 화면 위 이름 배지 — 전환할 수 있으면 눌러서 메뉴 */
export function AccountBadge({ name, style }: { name?: string; style?: CSSProperties }) {
  const [me, setMe] = useState<Me | null>(stored());
  const [open, setOpen] = useState(false);
  // 2026-10-27 지도사 「오재인 화면으로 전환해서 보는 기능 어디 있어」 — 예전에 로그인해 둔 브라우저는 저장된 계정에 슈퍼관리자 표시(isSuper)가 없어
  //   배지가 그냥 이름으로만 보였다 → 열 때 한 번 서버 계정(/api/auth/me)으로 맞춘다(실패하면 저장된 값 그대로)
  useEffect(() => {
    let live = true;
    let tok = '';
    try { tok = localStorage.getItem('token') || ''; } catch (e) { tok = ''; }
    if (!tok) return undefined;
    fetch(`${BASE}/me`, { headers: { Authorization: `Bearer ${tok}` } }).then(r => (r.ok ? r.json() : null)).then((d: Me | null) => {
      if (!live || !d || !d.id) return;
      const cur = stored() || {};
      // 누구로 쓰는지는 늘 서버(토큰) 기준 — 저장된 이름을 먼저 쓰면 다른 화면이 옛 계정 정보로 덮어쓴 뒤
      //   「임우승 계정으로 쓰는 중 · 임우승으로 돌아가기」처럼 엉뚱하게 보였다(2026-10-27 · 실제 토큰은 오재인)
      const next: Me = { ...cur, id: d.id, username: d.username, name: d.name, isSuper: !!d.isSuper, canSwitch: !!d.canSwitch, actingBy: d.actingBy || null };
      try { localStorage.setItem('account', JSON.stringify(next)); } catch (e) { /* 저장 못 해도 화면은 */ }
      setMe(next);
      try { window.dispatchEvent(new Event('account-sync')); } catch (e) { /* 아래 띠는 다음에 맞춰짐 */ }
    }).catch(() => undefined);
    return () => { live = false; };
  }, []);
  const ref = useRef<HTMLButtonElement | null>(null);
  const label = (me && me.actingBy ? me.name : '') || name || (me && me.name) || '';   // 전환 중이면 서버가 알려 준 이름(화면이 들고 있던 옛 이름 ✕)
  const base: CSSProperties = { padding: '3px 10px', borderRadius: 6, background: '#E8F0FE', color: '#2E75B6', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', ...style };
  if (!canSwitch(me)) return <span style={base}>{label}</span>;
  return (
    <>
      <button ref={ref} type="button" aria-haspopup="dialog" aria-expanded={open} title="다른 계정으로 쓰기" onClick={() => setOpen((v) => !v)}
        style={{ ...base, border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4, ...(me && me.actingBy ? { background: NAVY, color: '#fff' } : {}) }}>
        {label}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.85 }}>· 계정 전환</span><span aria-hidden style={{ fontSize: 9 }}>▾</span>
      </button>
      {open && <SwitchMenu onClose={() => setOpen(false)} anchor={ref.current ? ref.current.getBoundingClientRect() : null} />}
    </>
  );
}

/** 전환해 쓰는 동안 화면 아래에 늘 떠 있는 띠 */
export function ActingBar() {
  const [me, setMe] = useState<Me | null>(stored());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    // 다른 탭에서 바꾸거나 로그아웃하면 따라감
    const on = () => setMe(stored());
    window.addEventListener('storage', on);
    window.addEventListener('account-sync', on);   // 같은 탭에서 배지가 서버 계정으로 맞춘 뒤
    return () => { window.removeEventListener('storage', on); window.removeEventListener('account-sync', on); };
  }, []);
  // 띠도 열 때 한 번 서버(/me)로 맞춘다 — 배지가 없는 화면에서도 옛 저장값으로 「임우승 계정으로 쓰는 중 · 임우승으로 돌아가기」가 남지 않게(2026-10-27)
  useEffect(() => {
    let live = true;
    let tok = '';
    try { tok = localStorage.getItem('token') || ''; } catch (e) { tok = ''; }
    if (!tok) return undefined;
    fetch(`${BASE}/me`, { headers: { Authorization: `Bearer ${tok}` } }).then(r => (r.ok ? r.json() : null)).then((d: Me | null) => {
      if (!live || !d || !d.id) return;
      const next: Me = { ...(stored() || {}), id: d.id, username: d.username, name: d.name, isSuper: !!d.isSuper, canSwitch: !!d.canSwitch, actingBy: d.actingBy || null };
      try { localStorage.setItem('account', JSON.stringify(next)); } catch (e) { /* 화면만 */ }
      setMe(next);
    }).catch(() => undefined);
    return () => { live = false; };
  }, []);
  // 지금 계정이 곧 원래 계정이면(이미 임우승) 띠를 띄우지 않는다
  if (!me || !me.actingBy || !localStorage.getItem('token') || (me.id && String(me.actingBy.id) === String(me.id))) return null;
  const back = async () => {
    if (busy || !me.actingBy) return;
    setBusy(true); setErr('');
    try { await switchTo(me.actingBy.id); } catch (e) {
      // 전환 토큰이 끝났으면 서버가 못 받아 줌 → 맡겨 둔 내 토큰으로(2026-10-28) · 그것도 없으면 다시 로그인
      if (backToOrigin()) { window.location.assign('/'); return; }
      setErr(e instanceof Error ? e.message : '돌아가지 못했어요'); setBusy(false);
    }
  };
  return (
    <div role="status" style={{ position: 'fixed', left: '50%', bottom: 12, transform: 'translateX(-50%)', zIndex: 2900, maxWidth: 'calc(100vw - 24px)', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 8px 8px 16px', borderRadius: 12, background: NAVY, color: '#fff', boxShadow: '0 8px 24px rgba(17,24,39,.25)', fontSize: 13 }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {/* 같은 이름 계정이 둘이면(예: 임우승 · 임우승) 아이디를 붙여 구분(2026-10-27 「임우승 계정으로 쓰는 중 · 임우승으로 돌아가기」) */}
        <b>{me.name}{me.actingBy.name === me.name && me.username ? `(${me.username})` : ''}</b> 계정으로 쓰는 중{err ? ` · ${err}` : ''}
      </span>
      <button type="button" onClick={() => void back()} disabled={busy}
        style={{ flexShrink: 0, height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: '#fff', color: NAVY, fontSize: 13, fontWeight: 700, cursor: busy ? 'progress' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
        {busy ? '돌아가는 중…' : `${ro(me.actingBy.name)} 돌아가기`}
      </button>
    </div>
  );
}
